import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Chip,
  Button,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useData } from '../../context/DataContext';
import { slugify, findDepartmentByIdOrSlug } from '../../utils/slugify';
import DepartmentHero from './DepartmentHero';
import {
  LocalHospital as LocalHospitalIcon,
  People as PeopleIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import DoctorCard from './DoctorCard';
import LoadingAnimation from './LoadingAnimation';
import { sanitizeHtml } from '../../utils/sanitizeHtml';

const DepartmentDetails = () => {
  const { departmentId } = useParams();
  const navigate = useNavigate();
  const { state } = useData();
  const { departments, doctors, hospitalInfo, departmentsLoaded } = state;

  // State for selected department
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  // Find the specific department from URL params or set first one (only active departments)
  useEffect(() => {
    // Filter to only active departments
    const activeDepartments = departments.filter(dept => dept.is_active === true || dept.is_active === 1);
    
    if (departmentId) {
      const dept = findDepartmentByIdOrSlug(activeDepartments, departmentId);
      setSelectedDepartment(dept || activeDepartments[0]);
    } else if (activeDepartments.length > 0) {
      setSelectedDepartment(activeDepartments[0]);
    }
  }, [departmentId, departments]);

  // Scroll to top instantly when department changes (before paint)
  React.useLayoutEffect(() => {
    // Disable browser's automatic scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    
    // Disable smooth scroll behavior for instant scroll
    const html = document.documentElement;
    const body = document.body;
    const htmlScrollBehavior = html.style.scrollBehavior;
    const bodyScrollBehavior = body.style.scrollBehavior;
    
    // Set to auto for instant scroll
    html.style.scrollBehavior = 'auto';
    body.style.scrollBehavior = 'auto';
    
    // Scroll to top immediately (multiple methods for cross-browser support)
    window.scrollTo(0, 0);
    window.scroll(0, 0);
    html.scrollTop = 0;
    body.scrollTop = 0;
    
    // Force a reflow to ensure scroll happens before paint
    void html.offsetHeight;
    
    // Restore smooth scroll after scroll completes
    requestAnimationFrame(() => {
      html.style.scrollBehavior = htmlScrollBehavior;
      body.style.scrollBehavior = bodyScrollBehavior;
    });
  }, [departmentId]);

  // Helper function to normalize department/specialty names for comparison
  const normalizeName = (name) => {
    if (!name) return '';
    // Remove common prefixes and normalize
    return name
      .replace(/^(Department\s+Of|Department)\s+/i, '') // Remove "Department Of" or "Department" prefix
      .trim()
      .toLowerCase();
  };

  // Filter doctors for selected department
  const departmentDoctors = selectedDepartment ? doctors.filter(doctor => {
    const normalizedDeptName = normalizeName(selectedDepartment.name);
    const normalizedSpecialty = normalizeName(doctor.specialty);
    
    // Only allow exact normalized matches (no partial/substring matching)
    return normalizedDeptName === normalizedSpecialty;
  }) : [];

  // Department-specific information
  const getDepartmentInfo = (departmentName) => {
    // If the department has custom information, use it
    if (selectedDepartment?.departmentInfo || selectedDepartment?.expertise || selectedDepartment?.services) {
      return {
        description: selectedDepartment.departmentInfo || selectedDepartment.description,
        services: selectedDepartment.services || 
          '<ul><li>Diagnostic Services</li><li>Treatment Plans</li><li>Preventive Care</li><li>Follow-up Care</li><li>Emergency Services</li></ul>',
        expertise: selectedDepartment.expertise || 'Our specialists are experienced healthcare professionals dedicated to providing the highest quality care.'
      };
    }

    // Fallback to hardcoded information if no custom data
    const info = {
      'Cardiology': {
        description: 'Our Cardiology Department provides comprehensive cardiovascular care using state-of-the-art technology and evidence-based treatments. We specialize in the diagnosis and treatment of heart diseases, including coronary artery disease, heart failure, arrhythmias, and congenital heart defects.',
        services: '<ul><li>Cardiac catheterization and angioplasty</li><li>Electrocardiogram (ECG) and stress testing</li><li>Echocardiography and cardiac imaging</li><li>Pacemaker and defibrillator implantation</li><li>Cardiac rehabilitation programs</li><li>Preventive cardiology and risk assessment</li></ul>',
        expertise: 'Our cardiologists are board-certified specialists with expertise in interventional cardiology, electrophysiology, and heart failure management.'
      },
      'Neurology': {
        description: 'The Neurology Department offers advanced neurological care for patients with disorders of the brain, spinal cord, and nervous system. We provide comprehensive evaluation, diagnosis, and treatment for a wide range of neurological conditions.',
        services: '<ul><li>Stroke diagnosis and treatment</li><li>Epilepsy and seizure management</li><li>Multiple sclerosis treatment</li><li>Parkinson\'s disease care</li><li>Neuromuscular disorders</li><li>Headache and migraine treatment</li></ul>',
        expertise: 'Our neurologists specialize in stroke care, movement disorders, epilepsy, and neuromuscular diseases with access to advanced diagnostic imaging.'
      },
      'Pediatrics': {
        description: 'Our Pediatrics Department provides compassionate, family-centered care for children from birth through adolescence. We focus on preventive care, early intervention, and comprehensive treatment for pediatric conditions.',
        services: '<ul><li>Well-child visits and vaccinations</li><li>Acute illness treatment</li><li>Chronic disease management</li><li>Developmental assessments</li><li>Behavioral health services</li><li>Adolescent medicine</li></ul>',
        expertise: 'Our pediatricians are experienced in general pediatrics, adolescent medicine, and developmental pediatrics with child-friendly facilities.'
      },
      'Internal Medicine': {
        description: 'The Internal Medicine Department provides comprehensive adult medical care, focusing on prevention, diagnosis, and treatment of adult diseases. We serve as primary care physicians and coordinate care with specialists.',
        services: '<ul><li>Preventive care and health screenings</li><li>Chronic disease management</li><li>Acute illness treatment</li><li>Health maintenance and wellness</li><li>Geriatric care</li><li>Complex medical conditions</li></ul>',
        expertise: 'Our internists are board-certified specialists in adult medicine with expertise in managing complex medical conditions and coordinating multidisciplinary care.'
      },
      'Dermatology': {
        description: 'Our Dermatology Department provides comprehensive skin care services for patients of all ages. We diagnose and treat various skin conditions, perform skin cancer screenings, and offer cosmetic dermatology procedures.',
        services: '<ul><li>Skin cancer screening and treatment</li><li>Acne and rosacea treatment</li><li>Eczema and psoriasis management</li><li>Surgical dermatology procedures</li><li>Cosmetic dermatology</li><li>Allergy testing and treatment</li></ul>',
        expertise: 'Our dermatologists are experts in medical, surgical, and cosmetic dermatology with access to advanced diagnostic and treatment technologies.'
      },
      'Endocrinology': {
        description: 'The Endocrinology Department specializes in the diagnosis and treatment of hormonal disorders and metabolic diseases. We provide comprehensive care for diabetes, thyroid disorders, and other endocrine conditions.',
        services: '<ul><li>Diabetes management and education</li><li>Thyroid disorder treatment</li><li>Hormone replacement therapy</li><li>Metabolic syndrome care</li><li>Osteoporosis treatment</li><li>Adrenal and pituitary disorders</li></ul>',
        expertise: 'Our endocrinologists are specialists in diabetes care, thyroid disorders, and metabolic diseases with expertise in the latest treatment protocols.'
      },
      'Gastroenterology': {
        description: 'Our Gastroenterology Department provides comprehensive digestive health care, specializing in the diagnosis and treatment of disorders affecting the gastrointestinal tract, liver, and pancreas.',
        services: '<ul><li>Endoscopy and colonoscopy</li><li>Liver disease treatment</li><li>Inflammatory bowel disease care</li><li>Gastrointestinal cancer screening</li><li>Digestive disorder treatment</li><li>Nutritional counseling</li></ul>',
        expertise: 'Our gastroenterologists are experts in diagnostic and therapeutic endoscopy, liver disease, and inflammatory bowel disease with state-of-the-art equipment.'
      },
      'Pulmonology': {
        description: 'The Pulmonology Department specializes in the diagnosis and treatment of respiratory and lung disorders. We provide comprehensive care for patients with breathing difficulties and lung diseases.',
        services: '<ul><li>Asthma and COPD treatment</li><li>Sleep apnea diagnosis and treatment</li><li>Lung cancer screening</li><li>Pulmonary function testing</li><li>Respiratory therapy</li><li>Interstitial lung disease care</li></ul>',
        expertise: 'Our pulmonologists are specialists in respiratory medicine with expertise in sleep medicine, critical care, and interventional pulmonology.'
      },
      'Oncology': {
        description: 'Our Oncology Department provides comprehensive cancer care with a multidisciplinary approach. We offer diagnosis, treatment, and supportive care for various types of cancer using the latest evidence-based protocols.',
        services: '<ul><li>Cancer diagnosis and staging</li><li>Chemotherapy and immunotherapy</li><li>Radiation therapy coordination</li><li>Surgical oncology consultation</li><li>Palliative care services</li><li>Cancer survivorship programs</li></ul>',
        expertise: 'Our oncologists are board-certified specialists with expertise in medical oncology, hematology, and personalized cancer treatment plans.'
      },
      'Psychiatry': {
        description: 'The Psychiatry Department provides comprehensive mental health care for patients of all ages. We offer diagnosis, treatment, and ongoing support for various psychiatric conditions and mental health disorders.',
        services: '<ul><li>Depression and anxiety treatment</li><li>Bipolar disorder management</li><li>Schizophrenia treatment</li><li>Addiction and substance abuse care</li><li>Child and adolescent psychiatry</li><li>Geriatric psychiatry</li></ul>',
        expertise: 'Our psychiatrists are specialists in adult, child, and geriatric psychiatry with expertise in medication management and psychotherapy.'
      },
      'General Surgery': {
        description: 'Our General Surgery Department provides comprehensive surgical care for a wide range of conditions. We perform both elective and emergency surgeries using minimally invasive techniques when possible.',
        services: [
          'Laparoscopic surgery',
          'Hernia repair',
          'Gallbladder surgery',
          'Appendectomy',
          'Breast surgery',
          'Emergency trauma surgery'
        ],
        expertise: 'Our general surgeons are experienced in both traditional and minimally invasive surgical techniques with expertise in emergency and elective procedures.'
      },
      'Cardiac Surgery': {
        description: 'The Cardiac Surgery Department provides advanced surgical treatment for complex heart conditions. We perform a wide range of cardiac procedures using the latest surgical techniques and technology.',
        services: [
          'Coronary artery bypass grafting',
          'Heart valve repair and replacement',
          'Aortic aneurysm repair',
          'Congenital heart defect surgery',
          'Heart transplant evaluation',
          'Minimally invasive cardiac surgery'
        ],
        expertise: 'Our cardiac surgeons are board-certified specialists with expertise in complex cardiac procedures and minimally invasive techniques.'
      },
      'Orthopedic Surgery': {
        description: 'Our Orthopedic Surgery Department specializes in the diagnosis and treatment of musculoskeletal conditions. We provide surgical and non-surgical treatment for bone, joint, and muscle disorders.',
        services: [
          'Joint replacement surgery',
          'Sports medicine procedures',
          'Spine surgery',
          'Fracture treatment',
          'Arthroscopic surgery',
          'Hand and foot surgery'
        ],
        expertise: 'Our orthopedic surgeons are specialists in joint replacement, sports medicine, spine surgery, and trauma with expertise in both surgical and non-surgical treatments.'
      },
      'Neurosurgery': {
        description: 'The Neurosurgery Department provides advanced surgical treatment for disorders of the brain, spine, and nervous system. We use the latest technology and techniques to provide optimal outcomes.',
        services: [
          'Brain tumor surgery',
          'Spine surgery and fusion',
          'Cerebrovascular surgery',
          'Epilepsy surgery',
          'Trauma neurosurgery',
          'Minimally invasive spine surgery'
        ],
        expertise: 'Our neurosurgeons are board-certified specialists with expertise in complex brain and spine surgery using advanced imaging and surgical techniques.'
      },
      'Obstetrics & Gynecology': {
        description: 'Our Obstetrics & Gynecology Department provides comprehensive women\'s health care throughout all stages of life. We offer obstetric care, gynecological services, and specialized women\'s health procedures.',
        services: [
          'Prenatal care and delivery',
          'Gynecological surgery',
          'Family planning services',
          'Menopause management',
          'Gynecological oncology',
          'Reproductive endocrinology'
        ],
        expertise: 'Our OB/GYN specialists are experienced in high-risk obstetrics, gynecological surgery, and women\'s health with a focus on personalized care.'
      },
      'Ophthalmology': {
        description: 'The Ophthalmology Department provides comprehensive eye care services for patients of all ages. We diagnose and treat various eye conditions and perform surgical procedures to improve vision.',
        services: '<ul><li>Cataract surgery</li><li>Glaucoma treatment</li><li>Retinal disease management</li><li>Corneal surgery</li><li>Pediatric ophthalmology</li><li>Refractive surgery</li></ul>',
        expertise: 'Our ophthalmologists are specialists in medical and surgical eye care with expertise in cataract surgery, glaucoma treatment, and retinal diseases.'
      },
      'ENT Surgery': {
        description: 'Our ENT Surgery Department specializes in the diagnosis and treatment of disorders affecting the ear, nose, throat, head, and neck. We provide both medical and surgical treatment options.',
        services: '<ul><li>Sinus surgery</li><li>Tonsillectomy and adenoidectomy</li><li>Hearing loss treatment</li><li>Voice and swallowing disorders</li><li>Head and neck cancer surgery</li><li>Sleep apnea surgery</li></ul>',
        expertise: 'Our ENT surgeons are specialists in otolaryngology with expertise in head and neck surgery, sinus surgery, and hearing disorders.'
      },
      'Urology': {
        description: 'The Urology Department provides comprehensive care for disorders of the urinary tract and male reproductive system. We offer both medical and surgical treatment options for various urological conditions.',
        services: '<ul><li>Prostate surgery</li><li>Kidney stone treatment</li><li>Bladder cancer surgery</li><li>Incontinence treatment</li><li>Male infertility treatment</li><li>Minimally invasive urology</li></ul>',
        expertise: 'Our urologists are specialists in urological surgery with expertise in robotic surgery, minimally invasive procedures, and complex urological conditions.'
      },
      'Laboratory': {
        description: 'Our Laboratory Department provides comprehensive diagnostic testing services to support patient care. We offer a wide range of clinical laboratory tests with rapid turnaround times and high accuracy.',
        services: '<ul><li>Blood chemistry testing</li><li>Hematology and coagulation studies</li><li>Microbiology and culture testing</li><li>Immunology and allergy testing</li><li>Molecular diagnostics</li><li>Point-of-care testing</li></ul>',
        expertise: 'Our laboratory is staffed by certified medical technologists and pathologists with expertise in clinical laboratory science and quality assurance.'
      },
      'Radiology': {
        description: 'The Radiology Department provides advanced imaging services for diagnosis and treatment planning. We use state-of-the-art technology to provide high-quality diagnostic images and interventional procedures.',
        services: '<ul><li>X-ray and fluoroscopy</li><li>Computed tomography (CT)</li><li>Magnetic resonance imaging (MRI)</li><li>Ultrasound imaging</li><li>Nuclear medicine</li><li>Interventional radiology</li></ul>',
        expertise: 'Our radiologists are board-certified specialists with expertise in diagnostic imaging, interventional radiology, and nuclear medicine.'
      },
      'Pathology': {
        description: 'Our Pathology Department provides comprehensive diagnostic pathology services to support patient care. We analyze tissue samples, blood, and body fluids to help diagnose diseases and guide treatment.',
        services: '<ul><li>Surgical pathology</li><li>Cytopathology</li><li>Hematopathology</li><li>Molecular pathology</li><li>Autopsy services</li><li>Forensic pathology</li></ul>',
        expertise: 'Our pathologists are board-certified specialists with expertise in surgical pathology, cytopathology, and molecular diagnostics.'
      },
      'Nuclear Medicine': {
        description: 'The Nuclear Medicine Department provides specialized imaging and treatment services using radioactive materials. We offer diagnostic imaging and therapeutic procedures for various medical conditions.',
        services: '<ul><li>PET/CT scanning</li><li>Bone scanning</li><li>Thyroid imaging and treatment</li><li>Cardiac nuclear imaging</li><li>Renal nuclear medicine</li><li>Radioactive therapy</li></ul>',
        expertise: 'Our nuclear medicine specialists are board-certified with expertise in diagnostic nuclear imaging and therapeutic nuclear medicine procedures.'
      },
      'Critical Care': {
        description: 'Our Critical Care Department provides intensive care for patients with life-threatening conditions. We offer 24/7 monitoring and treatment in our state-of-the-art intensive care units.',
        services: '<ul><li>Mechanical ventilation</li><li>Hemodynamic monitoring</li><li>Sepsis management</li><li>Trauma care</li><li>Post-operative care</li><li>Emergency procedures</li></ul>',
        expertise: 'Our critical care specialists are board-certified intensivists with expertise in managing complex medical and surgical conditions in critically ill patients.'
      }
    };

    return info[departmentName] || {
      description: selectedDepartment?.description || '',
      services: '<ul><li>Diagnostic Services</li><li>Treatment Plans</li><li>Preventive Care</li><li>Follow-up Care</li><li>Emergency Services</li></ul>',
      expertise: 'Our specialists are experienced healthcare professionals dedicated to providing the highest quality care.'
    };
  };

  const handleDepartmentSelect = (department) => {
    setSelectedDepartment(department);
    // Use navigate to properly trigger route change and scroll-to-top
    navigate(`/department/${slugify(department.name)}`, { replace: true });
  };



  // Show loader while departments are being loaded
  if (!departmentsLoaded) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2
        }}
      >
        <LoadingAnimation sx={{ width: { xs: 280, sm: 360 }, maxWidth: 'min(90vw, 400px)' }} />
        <Typography variant="body1" color="text.secondary">
          Loading department information...
        </Typography>
      </Box>
    );
  }

  // Only show "No Departments Available" after data has loaded and there's actually no department
  if (!selectedDepartment) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>
          No Departments Available
        </Typography>
        <Button 
          variant="contained" 
          onClick={() => navigate(-1)}
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
      </Container>
    );
  }

  const departmentInfo = getDepartmentInfo(selectedDepartment.name);

  // Helper function to check if HTML content is actually empty (strips HTML tags)
  const hasTextContent = (htmlString) => {
    if (!htmlString) return false;
    // Create a temporary DOM element to extract text content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    return textContent.trim().length > 0;
  };

  return (
    <Box>
      {/* Hero Section */}
      <DepartmentHero department={selectedDepartment} showBackButton={true} />

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ py: 6 }}>
        <Grid container spacing={4}>
          {/* Left Sidebar - Department List */}
          <Grid item xs={12} md={4} lg={3}>
            <Card sx={{ position: 'sticky', top: 20 }}>
              <CardContent>
                <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                  All Departments
                </Typography>
                <List sx={{ p: 0 }}>
                  {departments.filter(dept => dept.is_active === true || dept.is_active === 1).map((department) => (
                    <ListItem key={department.id} disablePadding sx={{ mb: 1 }}>
                      <ListItemButton
                        selected={selectedDepartment.id === department.id}
                        onClick={() => handleDepartmentSelect(department)}
                        sx={{
                          borderRadius: 1,
                          '&.Mui-selected': {
                            backgroundColor: 'primary.main',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'primary.dark',
                            }
                          }
                        }}
                      >
                        <ListItemIcon sx={{ 
                          color: selectedDepartment.id === department.id ? 'white' : 'primary.main',
                          minWidth: 40
                        }}>
                          <LocalHospitalIcon />
                        </ListItemIcon>
                        <ListItemText 
                          primary={department.name}
                          primaryTypographyProps={{
                            fontSize: '0.9rem',
                            fontWeight: selectedDepartment.id === department.id ? 'bold' : 'normal'
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Side - Department Details */}
          <Grid item xs={12} md={8} lg={9}>
            {/* Department Overview */}
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                  {selectedDepartment.name}
                </Typography>
                <Typography 
                  variant="body1" 
                  paragraph 
                  sx={{ lineHeight: 1.8, fontSize: '1.1rem' }}
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(departmentInfo.description) }}
                />
                
                {/* Our Expertise Section - Only show if expertise has actual text content */}
                {hasTextContent(selectedDepartment?.expertise) && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    
                    <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Our Expertise
                    </Typography>
                    <Typography 
                      variant="body1" 
                      paragraph 
                      sx={{ lineHeight: 1.8, fontSize: '1.1rem' }}
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedDepartment.expertise) }}
                    />
                  </>
                )}
                
                {/* Services We Offer Section - Only show if services has actual text content */}
                {hasTextContent(selectedDepartment?.services) && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    
                    <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Services We Offer
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ lineHeight: 1.8, fontSize: '1.1rem' }}
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedDepartment.services) }}
                    />
                  </>
                )}
              </CardContent>
            </Card>

            {/* Department Doctors - Only show if there are doctors */}
            {departmentDoctors.length > 0 && (
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Our Specialists
                  </Typography>
                  <Grid container spacing={3}>
                    {departmentDoctors.map((doctor) => (
                      <Grid item xs={12} sm={6} md={4} key={doctor.id}>
                        <DoctorCard 
                          doctor={doctor}
                          variant="department"
                          showActions={false}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* Call to Action */}
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Ready to Schedule an Appointment?
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                  Contact us to schedule an appointment with one of our specialists in {selectedDepartment.name}.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                  {hospitalInfo?.showContact && hospitalInfo?.contact && (
                    <Button 
                      variant="contained" 
                      size="large"
                      startIcon={<PhoneIcon />}
                      href={`tel:${hospitalInfo.contact.replace(/[^\d+]/g, '')}`}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                        }
                      }}
                    >
                      Call Now - {hospitalInfo.contact}
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default DepartmentDetails; 